import * as React from "react"

import { Box, FormControl, FormHelperText, InputLabel, OutlinedInput, OutlinedInputProps } from "@mui/material"
import useId from "@mui/material/utils/useId"
import { RichTextInput, RichTextValue } from "@src/components/RichText"
import { RichTextFieldProps } from "./interface"

type TypedOutlineInputProps = Omit<OutlinedInputProps, 'value' | 'onChange'> & {
  value: RichTextValue | null;
  onChange: (value: RichTextValue) => void;
};
type TypedOutlineInput = (props: TypedOutlineInputProps) => React.ReactElement;

// Value and OnChange do not have proper generic support, so we override those values to help our own devs
const TypedOutlineInputComponent: TypedOutlineInput = OutlinedInput as any;

export function RichTextField(props: RichTextFieldProps) {
  const { label, value, readOnly, required, disabled, error, onChange } = props;
  const elementId = useId();

  const textCount = props.maxLength ? `${value?.text?.length ?? 0} / ${props.maxLength}` : undefined;
  const isTextTooLong = !!(props.maxLength && (value?.text?.length ?? 0) > props.maxLength);

  // Use passed in helper text or default to text count
  const helperText = props.helperText ?? textCount;
  const helperError = (!!props.helperText && error) || (!props.helperText && isTextTooLong);

  return (
    <Box sx={{
      '.ProseMirror:focus': {
        outline: 'none',
      },
    }}>
      <FormControl variant="outlined" fullWidth error={error} required={required}>
        <InputLabel htmlFor={elementId} required={required} error={error}>{label}</InputLabel>
        <TypedOutlineInputComponent
          id={elementId}
          label={label}
          value={value}
          readOnly={readOnly}
          required={required}
          disabled={disabled}
          error={error}
          onChange={onChange}
          slots={{ input: RichTextInput }}
          sx={{ px: 1, py: 2 }}
        />
        <FormHelperText error={helperError}>{helperText}</FormHelperText>
      </FormControl>
    </Box>
  );
}
